import { atom } from 'jotai/vanilla';
import { atomWithStorage } from 'jotai/vanilla/utils';

/**
 * Score entry interface
 */
export interface ScoreEntry {
  name: string;
  score: number;
  timestamp: number;
}

/**
 * All highscores (keyed by game key)
 */
export type AllHighscores = Record<string, ScoreEntry[]>;

/**
 * Highscores atom with localStorage persistence
 * Key: spillesammen:highscores
 *
 * Stores top 50 scores per game
 */
export const highscoresAtom = atomWithStorage<AllHighscores>('spillesammen:highscores', {});

/**
 * Create a derived atom for a specific game's highscores
 *
 * @param gameKey - The unique key for the game
 * @returns Atom for reading/writing highscores for this game
 */
export const createGameHighscoresAtom = (gameKey: string) =>
  atom(
    // Getter - return scores for this game, sorted
    (get) => {
      const allScores = get(highscoresAtom);
      const scores = allScores[gameKey] || [];
      return scores.sort((a, b) => b.score - a.score);
    },
    // Setter - add a new score entry
    (get, set, newEntry: ScoreEntry) => {
      const allScores = get(highscoresAtom);
      const currentScores = allScores[gameKey] || [];

      // Add new score and sort
      const updatedScores = [...currentScores, newEntry];
      updatedScores.sort((a, b) => b.score - a.score);

      // Keep only top 50
      const topScores = updatedScores.slice(0, 50);

      set(highscoresAtom, {
        ...allScores,
        [gameKey]: topScores,
      });
    }
  );
